import React from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { RootState } from "store";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import Button from "components/atoms/Button";
import { PrescriptionGroup, PrescriptionSource } from "common/models/types";
import { Flex, InfoIconButton, Spinner } from "components/atoms";
import { MIN_APPROVAL_INFO_COUNT } from "common/constants";
import { SOURCE_ICON } from "common/constants/prescriptionSource";
import { canShowDrugLabel } from "components/molecules/DrugLabelInfo/drugLabelButton";
import { openDrugLabelSlide } from "components/molecules/DrugLabelInfo/openDrugLabelSlide";
import { DrugNameLink } from "components/molecules/DrugLabelInfo/DrugNameLink";

interface PrescriptionGroupCardProps {
  group: PrescriptionGroup;
  referenceChecker?: {
    [id: string]: {
      count: number;
      totalLength?: number;
      totalBytes?: number;
    };
  };
  onEdit?: (groupId: string) => void;
  onDelete?: (groupId: string) => void;
  hideHospital?: boolean;
  /** 읽기전용(보고서화면): 수정/삭제 버튼 미렌더 */
  readOnly?: boolean;
}

const Card = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[200]};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const Header = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")[50]};
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Content = styled.div`
  padding: 16px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MedicationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

function formatKoreanDate(dateInput?: string | Date) {
  if (!dateInput) return "-";
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return String(dateInput);
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

const PrescriptionGroupCard: React.FC<PrescriptionGroupCardProps> = ({
  group,
  referenceChecker,
  onEdit,
  onDelete,
  hideHospital,
  readOnly,
}) => {
  // 이번 세션에 허가사항을 열어본(캐시된) 약은 약품명을 회색 처리 (visited link 패턴)
  const drugLabels = useSelector((s: RootState) => s.data.drugLabels);
  return (
    <Card>
      <Header>
        <div>
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[800]} style={{ fontWeight: 500 }}>
            {formatKoreanDate(group.prescriptionDate)}
          </Text>
          {!hideHospital && (
            <>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[400]} style={{ margin: "0 8px" }}>
                |
              </Text>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
                {group.hospital || "-"}
              </Text>
            </>
          )}
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[400]} style={{ margin: "0 8px" }}>
            |
          </Text>
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
            {group.prescriptions.length}약품
          </Text>
        </div>
        {!readOnly && (
          <ActionButtons>
            <Button
              size="Small"
              type="Icon"
              variant="Outlined"
              color="Secondary"
              icon="Edit 01"
              onClick={() => onEdit?.(group.groupId)}
            />
            <Button
              size="Small"
              type="Icon"
              variant="Outlined"
              color="Negative"
              icon="Trash"
              onClick={() => onDelete?.(group.groupId)}
            />
          </ActionButtons>
        )}
      </Header>
      <Content>
        {group.prescriptions.map((prescription, index) => {
          // readOnly(보고서 뷰어)에선 슬라이더 비활성 — 보고서엔 이미 허가사항 탭이 있음
          const showLabel = !readOnly && canShowDrugLabel(prescription.itemCode, referenceChecker);
          const visited =
            !!prescription.itemCode && drugLabels[prescription.itemCode] !== undefined;
          return (
          <MedicationItem key={index}>
            <Flex align="center" gap="8px">
              <Text
                textType="Body1"
                color={COLOR_SYSTEM.get("Gray")[500]}
                style={{ 
                  fontSize: "11px", 
                  fontWeight: 500, 
                  minWidth: "18px",
                  textAlign: "center"
                }}
              >
                {index + 1}
              </Text>
              <DrugNameLink
                $clickable={showLabel}
                $visited={visited}
                onClick={
                  showLabel
                    ? () =>
                        openDrugLabelSlide({
                          itemCode: prescription.itemCode!,
                          productName: prescription.medicationName,
                        })
                    : undefined
                }
              >
                <Text textType="Body1" style={{ fontWeight: 500 }}>
                  {prescription.medicationName}
                </Text>
              </DrugNameLink>
              {prescription.source && (prescription.source as string) in SOURCE_ICON && (
                <span title={SOURCE_ICON[prescription.source as PrescriptionSource].label} style={{ display: "inline-flex", alignItems: "center" }}>
                  <Icon name={SOURCE_ICON[prescription.source as PrescriptionSource].name} size={13} color={COLOR_SYSTEM.get("Gray")[400]} />
                </span>
              )}
              {!prescription.itemCode && (
                <InfoIconButton
                  iconName="Information Circle"
                  iconColor={COLOR_SYSTEM.get("Red")[400]}
                  tooltip="의약품이 정확히 입력되지 않았습니다. 수정 버튼을 눌러 의약품명을 검색하고 목록에서 선택해야 합니다."
                />
              )}
              {prescription.itemCode && referenceChecker?.[prescription.itemCode]?.count === -1 && (
                <Spinner size="Small" />
              )}
              {prescription.itemCode &&
                referenceChecker?.[prescription.itemCode]?.count >= 0 &&
                referenceChecker?.[prescription.itemCode]?.count < MIN_APPROVAL_INFO_COUNT && (
                  <InfoIconButton
                    iconName="Information Circle"
                    iconColor={COLOR_SYSTEM.get("Red")[400]}
                    tooltip="허가사항 정보가 부족합니다. 클릭하여 식약처 사이트에서 취소/취하 여부를 확인하세요. 취소/취하된 경우 같은 성분의 다른 제품으로 변경해야 하며, 원래 정보가 적은 경우 그대로 진행 가능합니다."
                    onClick={() => {
                      window.open(
                        `https://nedrug.mfds.go.kr/pbp/CCBBB01/getItemDetail?itemSeq=${prescription.itemCode}`,
                        "_blank"
                      );
                    }}
                  />
                )}
            </Flex>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[600]}>
              {prescription.dosagePerAdministration}
              {prescription.dosageUnit}, {prescription.timesPerDay}회,{" "}
              {prescription.daysOfAdministration}일
              {prescription.usageInstruction && (
                <Text
                  textType="Body1"
                  color={COLOR_SYSTEM.get("Gray")[600]}
                  style={{ marginLeft: 8 }}
                >
                  ({prescription.usageInstruction})
                </Text>
              )}
            </Text>
          </MedicationItem>
          );
        })}
      </Content>
    </Card>
  );
};

export default PrescriptionGroupCard;
